import { Box, Card, Button, Container, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldDailyRecord, EldDriver, EldEventLine } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import { useParams, useRouter } from "src/routes/hooks";
import { useResponsive } from "src/hooks/use-responsive";
import { formatDateForPreview, formatDatetimeForPreview, formatTimeAsHourMin } from "src/utils/date-utils";
import { useAuthContext } from "src/auth/hooks";
import { Violations } from "src/components/violations";
import { EldDriverStatusLabel } from "src/components/eld-driver-status-label";
import { DateTime } from "luxon";

type WeeklyHoursWorked = {
  day: string,
  totalHoursDriving: number,
  totalHoursOnDuty: number,
  violations: string[],
  certified: boolean,
}

type EldDriverLogsSummary = {
  eldDriver: EldDriver,
  eldDailyRecord: EldDailyRecord,
  weeklyHoursWorked: WeeklyHoursWorked[] // always 15 values
}

type PageModel = {
  eldDriverLogsSummary?: EldDriverLogsSummary,
  lastActiveStatusEvent?: EldEventLine,
}

export default function Page() {
  const { eldDriverId } = useParams();

  const settings = useSettingsContext();
  const router = useRouter();
  const dataMaintenanceer = useCallback(() => ({
    eldDriverLogsSummary: axios.get(formatEndpoint(endpoints.eldDriverLogsSummary.findByEldDriverId, { eldDriverId })).then(response => response.data),
    lastActiveStatusEvent: axios.get(formatEndpoint(endpoints.eldEventLines.findLastActiveStatusEvent, { eldDriverId })).then(response => response.data),
  }), [eldDriverId]);


  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataMaintenanceer);

  useEffect(() => {
    if (!loading && error) {
      alert(error);
    }
  }, [loading, error]);

  const updatedAt = useMemo(() => {
    if (!data?.lastActiveStatusEvent && !data?.eldDriverLogsSummary?.eldDailyRecord?.updatedAt) {
      return undefined;
    }
    if (data?.lastActiveStatusEvent && data?.eldDriverLogsSummary?.eldDailyRecord?.updatedAt) {
      if (DateTime.fromISO(data.lastActiveStatusEvent.eventTime, { zone: "utc" }) > DateTime.fromISO(data.eldDriverLogsSummary.eldDailyRecord.updatedAt!)) {
        return data.lastActiveStatusEvent.eventTime;
      } 
    }
    return data?.eldDriverLogsSummary?.eldDailyRecord?.updatedAt;
  }, [data?.lastActiveStatusEvent, data?.eldDriverLogsSummary?.eldDailyRecord?.updatedAt]);

  const upMd = useResponsive('up', 'md');

  const [shownCount, setShownCount] = useState(14);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <Box sx={{ display: "flex" }}>
          {data && data?.eldDriverLogsSummary &&
            <Box width="100%">
              <PageHeader title={`${data.eldDriverLogsSummary.eldDriver.firstName} ${data.eldDriverLogsSummary.eldDriver.lastName}`} subtitle={<Box>
                <Stack direction="row" gap={2}>
                  {data.eldDriverLogsSummary.eldDriver.phone && <Box>
                    {data.eldDriverLogsSummary.eldDriver.phone}
                  </Box>}
                  {data.eldDriverLogsSummary.eldDriver.username && <Box>
                    {data.eldDriverLogsSummary.eldDriver.username}
                  </Box>}
                  {data.eldDriverLogsSummary.eldDriver.email && data.eldDriverLogsSummary.eldDriver.email !== data.eldDriverLogsSummary.eldDriver.username && <Box>
                    {data.eldDriverLogsSummary.eldDriver.email}
                  </Box>}
                </Stack>
              </Box>} />

              <Card sx={{ marginBottom: 2 }}>
                <Stack direction="column">
                  <Box display={upMd ? "flex" : undefined}>
                    <Table sx={{ tableLayout: "fixed" }} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">STATUS</TableCell>
                          <TableCell align="center">UPDATED</TableCell>
                          <TableCell align="center">VEHICLE</TableCell>
                          <TableCell align="center">LOCATION</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell align="center">
                            <EldDriverStatusLabel status={data.eldDriverLogsSummary.eldDailyRecord.lastStatusEvent} />
                          </TableCell>
                          <TableCell align="center">{formatDatetimeForPreview(updatedAt, timezone)}</TableCell>
                          <TableCell align="center">{data.eldDriverLogsSummary.eldDailyRecord.vehicleUnitNumber}</TableCell>
                          <TableCell align="center">{data.eldDriverLogsSummary.eldDailyRecord.lastLocation}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    <Table sx={{ tableLayhout: "fixed" }} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">BREAK</TableCell>
                          <TableCell align="center">DRIVE</TableCell>
                          <TableCell align="center">SHIFT</TableCell>
                          <TableCell align="center">CYCLE</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" sx={{ fontSize: "18px", fontWeight: 600 }}>
                            {formatTimeAsHourMin(data.eldDriverLogsSummary.eldDailyRecord.breakTime)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: "18px", fontWeight: 600 }}>
                            {formatTimeAsHourMin(data.eldDriverLogsSummary.eldDailyRecord.driveTime)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: "18px", fontWeight: 600 }}>
                            {formatTimeAsHourMin(data.eldDriverLogsSummary.eldDailyRecord.shiftTime)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: "18px", fontWeight: 600 }}>
                            {formatTimeAsHourMin(data.eldDriverLogsSummary.eldDailyRecord.cycleTime)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                </Stack>
              </Card>

              <Box component="h2" sx={{ pt: 1 }}>Logs</Box>

              <Card>
                <Stack direction="column">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>DAY</TableCell>
                        <TableCell align="right">HOURS DROVE</TableCell>
                        <TableCell align="right">HOURS ON DUTY</TableCell>
                        <TableCell>VIOLATIONS</TableCell>
                        <TableCell>CERTIFIED</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.eldDriverLogsSummary.weeklyHoursWorked.map((whw, i) => i < shownCount ? <TableRow
                        key={i}
                        hover
                        onClick={() => router.push(`/app/eld/eld-driver-logs/single/${eldDriverId}/${whw.day}`)}
                        sx={{ cursor: "pointer" }}>
                        <TableCell>{formatDateForPreview(whw.day)}</TableCell>
                        <TableCell align="right">{whw.totalHoursDriving.toFixed(2)}</TableCell>
                        <TableCell align="right">{whw.totalHoursOnDuty.toFixed(2)}</TableCell>
                        <TableCell><Violations violations={whw.violations} /></TableCell>
                        <TableCell>{!whw.certified ? "Not Certified" : ""}</TableCell>
                      </TableRow> : undefined)}
                    </TableBody>
                  </Table>

                  {shownCount < 35 && <Button size="small" variant="text" onClick={() => setShownCount(shownCount + 7)}>
                    Show more
                  </Button>}
                </Stack>
              </Card>
            </Box>
          }
        </Box>
      </DisplayPageData>
    </Container>
  );
}
